import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import EditionEngagement from './EditionEngagement';
import EditionSendProgress from './EditionSendProgress';
import StatusChip from '../StatusChip';
import { AWAITING_DEPLOY, EDITION_STATUS, editionTitle, formatDateTime, isEditionEditable } from '../../changelogAdminConfig';

const Info = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
  </Box>
);

/**
 * Um envio na lista: número, nome, status, datas, itens, progresso da fila e métricas.
 * O "Próximo envio" (rascunho sempre presente) aparece em destaque no topo.
 * @param {{ edition: Object, isNext?: boolean, onEdit: Function, onPreview: Function, onShowRecipients: Function }} props
 */
function EditionCard({ edition, isNext = false, onEdit, onPreview, onShowRecipients }) {
  const status = EDITION_STATUS[edition.status] || EDITION_STATUS.draft;
  const editable = isEditionEditable(edition);
  const blocked = edition.blocked_items || 0;
  const hasSends = (edition.sends?.total || 0) > 0;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 2.5 }, mb: 2, borderRadius: '12px',
        border: isNext ? '1px solid #C7D4FE' : '1px solid #E2E8F0',
        backgroundColor: isNext ? '#FBFCFF' : '#fff',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
            {isNext && <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', textTransform: 'uppercase', letterSpacing: 0.4 }}>Próximo envio</Typography>}
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>{editionTitle(edition)}</Typography>
            <StatusChip info={status} />
            {blocked > 0 && <StatusChip info={{ ...AWAITING_DEPLOY, label: `${blocked} aguardando deploy` }} />}
          </Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.35 }}>{edition.subject || 'Sem assunto'}</Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {hasSends && (
            <Button size="small" variant="outlined" startIcon={<PeopleOutlineOutlinedIcon />} onClick={() => onShowRecipients(edition)}>
              Destinatários
            </Button>
          )}
          <Button size="small" variant="outlined" startIcon={<VisibilityOutlinedIcon />} onClick={() => onPreview(edition)}>
            Prévia
          </Button>
          {editable && (
            <Button size="small" variant="contained" startIcon={<EditOutlinedIcon />} onClick={() => onEdit(edition)}>
              {isNext ? 'Montar envio' : 'Editar'}
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: { xs: 2, md: 4 }, flexWrap: 'wrap', mt: 2 }}>
        <Info label="Itens" value={edition.item_count ?? 0} />
        <Info label="Destaques" value={edition.highlight_count ?? 0} />
        <Info label="Vídeo" value={edition.has_video ? 'Sim' : 'Não'} />
        {edition.scheduled_at && edition.status !== 'sent' && <Info label="Agendado para" value={formatDateTime(edition.scheduled_at)} />}
        {edition.sent_at && <Info label="Enviado em" value={formatDateTime(edition.sent_at)} />}
        {hasSends && <Info label="Enviados" value={edition.sends.sent || 0} />}
        {(edition.sends?.pending || 0) > 0 && <Info label="Pendentes" value={edition.sends.pending} />}
      </Box>

      {edition.last_error && edition.status === 'scheduled' && (
        <Alert severity="error" sx={{ mt: 2 }}>Não saiu na hora marcada: {edition.last_error}</Alert>
      )}

      <EditionSendProgress sends={edition.sends} />
      <EditionEngagement edition={edition} />
    </Paper>
  );
}

export default EditionCard;
